import React, { useEffect } from 'react'
import ama2 from '../images/ama2.jpeg'

const About: React.FC = () => {
    useEffect(() => {
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -100px 0px'
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible')
                    observer.unobserve(entry.target)
                }
            })
        }, observerOptions)

        document.querySelectorAll('.reveal-on-scroll').forEach((el) => {
            observer.observe(el)
        })

        return () => observer.disconnect()
    }, [])

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
            {/* Hero Section */}
            <div className="pt-32 pb-20 bg-gradient-to-br from-brand/5 via-slate-50 to-blue-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16 reveal-on-scroll">
                        <div className="inline-block mb-6">
                            <span className="text-sm font-semibold text-brand tracking-widest uppercase">About Our Company</span>
                        </div>
                        <h1 className="text-6xl md:text-7xl font-display font-bold text-dark mb-6 leading-tight">
                            About <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand to-blue-600">GuardRisk</span>
                        </h1>
                        <p className="text-xl text-gray-body max-w-3xl mx-auto leading-relaxed">
                            Leading the insurance industry with integrity, expertise, and dedication to our clients. Protecting what matters most since inception.
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">

                {/* Mission & Vision */}
                <section className="py-20 mb-20">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="reveal-on-scroll group">
                            <div className="relative p-8 bg-gradient-to-br from-brand/10 to-brand/5 rounded-2xl border border-brand/10 hover:border-brand/30 transition-all duration-300 hover:shadow-lg h-full">
                                <div className="absolute top-0 left-0 w-1 h-16 bg-gradient-to-b from-brand to-transparent rounded-full"></div>
                                <div className="mb-6 inline-block p-3 bg-gradient-to-br from-brand/20 to-brand/10 rounded-lg">
                                    <svg className="w-6 h-6 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                </div>
                                <h2 className="text-3xl font-display font-bold text-dark mb-4">Our Mission</h2>
                                <p className="text-gray-body leading-relaxed mb-3">
                                    To provide comprehensive, personalized insurance solutions that protect what matters most to our clients while delivering exceptional value and service.
                                </p>
                                <p className="text-gray-body leading-relaxed">
                                    We believe insurance should be simple, transparent, and accessible to everyone.
                                </p>
                            </div>
                        </div>

                        <div className="reveal-on-scroll group">
                            <div className="relative p-8 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl border border-blue-100/50 hover:border-blue-200 transition-all duration-300 hover:shadow-lg h-full">
                                <div className="absolute top-0 left-0 w-1 h-16 bg-gradient-to-b from-blue-600 to-transparent rounded-full"></div>
                                <div className="mb-6 inline-block p-3 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-lg">
                                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                </div>
                                <h2 className="text-3xl font-display font-bold text-dark mb-4">Our Vision</h2>
                                <p className="text-gray-body leading-relaxed mb-3">
                                    To be the most trusted insurance broker in the region, known for our integrity and commitment to excellence.
                                </p>
                                <p className="text-gray-body leading-relaxed">
                                    We aim to set industry standards for customer service and professional expertise.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Founder Section */}
                <section className="mb-20 reveal-on-scroll">
                    <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl overflow-hidden">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 p-12 items-center">
                            <div className="reveal-on-scroll">
                                <div className="relative">
                                    <div className="absolute -inset-1 bg-gradient-to-r from-brand via-blue-500 to-cyan-500 rounded-3xl blur-2xl opacity-30"></div>
                                    <img
                                        src={ama2}
                                        alt="Hannah Ama Koomson"
                                        className="relative rounded-3xl shadow-2xl w-full h-auto object-cover"
                                    />
                                </div>
                            </div>
                            <div className="reveal-on-scroll">
                                <div className="inline-block mb-4">
                                    <span className="text-xs font-semibold text-brand tracking-widest uppercase bg-brand/10 px-3 py-1 rounded-full">Managing Director/CEO</span>
                                </div>
                                <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-6 leading-tight">
                                    Hannah Ama Koomson
                                </h2>
                                <div className="space-y-4 text-gray-300 leading-relaxed">
                                    <p>
                                        A distinguished and accomplished chartered insurance broker with over a decade of expertise, catering to the needs of both international and local companies. She has become a sought-after expert in providing insurance and risk management advice that leaves a lasting impact.
                                    </p>
                                    <p>
                                        Her specialization lies in general insurance, serving the distinct needs of public and private sector businesses. Ama is renowned for her commitment to understanding clients' concerns and crafting bespoke solutions with precision.
                                    </p>
                                    <p>
                                        Holder of a Master of Science and Bachelor of Science in International Business and Business Administration from the University of Ghana Business School. She actively contributes to the Chartered Insurance Institute (UK & Ghana) and represents Ghana as a country representative for Women Leadership in Insurance Africa.
                                    </p>
                                    <p className="text-brand font-semibold">
                                        Beyond business, she mentors young professionals and champions women's empowerment across the continent.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Team */}
                <section className="mb-20">
                    <div className="text-center mb-16 reveal-on-scroll">
                        <h2 className="text-5xl md:text-6xl font-display font-bold text-dark mb-4">
                            Our Leadership
                        </h2>
                        <p className="text-xl text-gray-body max-w-2xl mx-auto">
                            Exceptional talent dedicated to your protection
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { name: 'Hannah Ama Koomson', role: 'Managing Director', image: ama2 }
                        ].map((member, idx) => (
                            <div
                                key={idx}
                                className="reveal-on-scroll group"
                                style={{ transitionDelay: `${idx * 100}ms` }}
                            >
                                <div className="relative overflow-hidden rounded-2xl bg-white border border-slate-100 hover:border-brand/20 transition-all duration-300 hover:shadow-xl">
                                    <div className="relative h-72 overflow-hidden">
                                        <img
                                            src={member.image}
                                            alt={member.name}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                    </div>
                                    <div className="p-6">
                                        <h3 className="text-2xl font-display font-bold text-dark mb-2">{member.name}</h3>
                                        <p className="text-brand font-semibold text-sm uppercase tracking-widest">{member.role}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Values */}
                <section className="reveal-on-scroll">
                    <div className="text-center mb-16 reveal-on-scroll">
                        <h2 className="text-5xl md:text-6xl font-display font-bold text-dark mb-4">
                            Our Core Values
                        </h2>
                        <p className="text-xl text-gray-body max-w-2xl mx-auto">
                            Guiding principles that define everything we do
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            {
                                title: 'Integrity',
                                description: 'Honest and transparent in all dealings',
                                icon: '🛡️',
                                gradient: 'from-blue-500 to-cyan-500'
                            },
                            {
                                title: 'Excellence',
                                description: 'Committed to highest standards',
                                icon: '⭐',
                                gradient: 'from-purple-500 to-pink-500'
                            },
                            {
                                title: 'Trust',
                                description: 'Built through consistent quality',
                                icon: '🤝',
                                gradient: 'from-emerald-500 to-teal-500'
                            },
                            {
                                title: 'Innovation',
                                description: 'Always improving our services',
                                icon: '💡',
                                gradient: 'from-orange-500 to-red-500'
                            }
                        ].map((value, idx) => (
                            <div
                                key={idx}
                                className="reveal-on-scroll group h-full"
                                style={{ transitionDelay: `${idx * 50}ms` }}
                            >
                                <div className="relative h-full p-8 bg-white rounded-2xl border border-slate-100 hover:border-slate-200 transition-all duration-300 hover:shadow-xl overflow-hidden">
                                    <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${value.gradient} opacity-5 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500`}></div>
                                    <div className="relative z-10">
                                        <div className={`text-5xl mb-4`}>{value.icon}</div>
                                        <h3 className="text-2xl font-display font-bold text-dark mb-3">{value.title}</h3>
                                        <p className="text-gray-body leading-relaxed">{value.description}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    )
}

export default About
